// User-friendly tooltips for all settings parameters
export const SETTING_TOOLTIPS = {
  // Model Configuration
  'models.llm_provider': 'Choose whether to use Amazon Bedrock (AWS cloud service) or connect directly to Anthropic\'s API. Bedrock is recommended for most users.',
  
  'models.bedrock_model_id': 'Select which AI model to use for document classification. Nova Pro provides the best balance of speed and accuracy, while Claude models offer more advanced reasoning.',
  
  'models.anthropic_model_id': 'Choose which Claude model to use when connecting directly to Anthropic. Claude Sonnet 4 offers the best performance for classification tasks.',
  
  'models.aws_region': 'Select the AWS data center region closest to your location for faster response times. US East (Virginia) is recommended for most users.',
  
  'models.max_tokens': 'Controls how long the AI\'s responses can be. Higher values allow more detailed explanations but take longer to process. Recommended: 4000-8000 for SCG extraction. 1000 tokens ≈ 750 words.',
  
  'models.temperature': 'Controls how creative vs. consistent the AI responses are. Lower values (like 0.1) give more consistent results, while higher values add creativity. Keep low for classification accuracy.',
  
  'models.embedding_model': 'Choose the AI model used to understand document meaning. MPNet Base provides the best balance of accuracy and speed for most documents.',
  
  'models.batch_size': 'How many documents to process at once. Higher numbers are faster but use more computer memory. 32 is a good balance for most systems.',
  
  'models.normalize_embeddings': 'Technical setting that makes document comparisons more accurate. Keep this enabled unless you have a specific reason to change it.',

  // Classification Configuration  
  'classification.classification_temperature': 'Controls how consistent classification decisions are. Lower values give more predictable results. Keep at 0.1 for reliable document classification.',
  
  'classification.use_examples': 'Include example classifications to help the AI understand what you\'re looking for. This usually improves accuracy, so keep it enabled.',
  
  'classification.max_prompt_length': 'Maximum document length the AI will analyze at once. Longer documents provide more context but take more time to process. 4000 characters ≈ 800 words.',
  
  'classification.confidence_threshold': 'Minimum confidence level required before showing a classification result. Higher values mean more reliable results but may reject more documents.',

  // Search Configuration
  'search.top_k': 'How many search results to show. More results give you more options but can be overwhelming. 10 is usually sufficient for most searches.',
  
  'search.enable_reranking': 'Re-organizes search results to put the most relevant ones first. This improves accuracy but adds a small processing delay.',
  
  'search.original_score_weight': 'How much to trust the basic search score vs. advanced AI analysis. Higher values rely more on keyword matching. Must add up to 1.0 with enhancement weight.',
  
  'search.hyde_boost_weight': 'How much to boost results using advanced AI analysis. Higher values give more intelligent results but may be less predictable. Must add up to 1.0 with original weight.',
  
  'search.similarity_threshold': 'How similar documents must be to appear in results. Higher values show only very relevant documents, lower values cast a wider net.',
  
  'search.similarity_metric': 'Mathematical method for comparing documents. Cosine similarity works best for most text documents and is the recommended choice.',

  // Enhancement Configuration
  'hyde.enabled': 'Enables advanced AI analysis that helps find more relevant documents by understanding context and meaning, not just keywords.',
  
  'hyde.num_hypotheticals': 'How many different AI-generated examples to create for better searching. More examples find more documents but take longer to process.',
  
  'hyde.hyde_temperature': 'Controls variety in AI-generated search examples. Higher values create more diverse examples, which can find more documents but may be less focused.',
  
  'hyde.max_tokens': 'Maximum length of AI-generated search examples. Longer examples provide more detail but take more time to process. 1500 tokens ≈ 1100 words.',
  
  'hyde.combination_strategy': 'How to combine different search methods. Weighted average gives the most balanced results and is recommended for most users.',
  
  'hyde.original_weight': 'How much to trust your original search vs. AI-generated examples. Higher values stick closer to your exact words, lower values explore more possibilities.',

  // Processing Configuration
  'processing.ocr_enabled': 'Automatically reads text from scanned documents and images. Essential for processing PDFs that contain scanned pages instead of searchable text.',
  
  'processing.ocr_dpi': 'Image quality for reading scanned documents. Higher values read text more accurately but take much longer to process. 300 DPI works well for most documents.',
  
  'processing.chunk_size': 'How much text to analyze at once for long documents. Larger chunks provide more context but use more processing power. 1000 characters ≈ 200 words.',
  
  'processing.chunk_overlap': 'How much text to repeat between sections when processing long documents. This prevents important information from being split awkwardly.',
  
  'processing.processing_timeout': 'How long to wait before giving up on processing a document. Longer timeouts handle large files better but may freeze the system longer if something goes wrong.',

  // Query Configuration
  'query.rewriting_temperature': 'How much to change your search queries for better results. Lower values stay closer to your original words, higher values explore more alternatives.',
  
  'query.max_rewrite_tokens': 'Maximum length of rewritten search queries. Longer queries can be more specific but take more time to process. 800 tokens ≈ 600 words.',
  
  'query.num_variations': 'How many different versions of your search to try. More variations find more results but take longer to process.',
  
  'query.variation_temperature': 'How different the search variations should be from your original query. Higher values explore more possibilities but may drift from your intent.',
  
  'query.enable_auto_rewrite': 'Automatically improves your search queries without asking. This can find better results but may change your search in unexpected ways.',

  // SCG Configuration
  'scg.semantic_enabled': 'Uses AI to understand the meaning of security rules, not just exact word matches. This finds more relevant rules but requires more processing power.',
  
  'scg.semantic_similarity_threshold': 'How closely a document must match security rules to be flagged. Higher values only flag obvious matches, lower values are more cautious.',
  
  'scg.max_scg_rules_in_prompt': 'Maximum number of security rules to consider for each document. More rules provide better coverage but slow down processing.',
  
  'scg.keyword_match_weight': 'How much to trust exact word matches vs. AI understanding of meaning. Higher values rely more on specific words. Must add up to 1.0 with semantic weight.',
  
  'scg.semantic_match_weight': 'How much to trust AI understanding of meaning vs. exact word matches. Higher values are more intelligent but less predictable. Must add up to 1.0 with keyword weight.',

  // Infrastructure Configuration
  'infrastructure.log_level': 'How much detail to record in system logs. INFO shows important events, DEBUG shows everything (useful for troubleshooting), ERROR shows only problems.',
  
  'infrastructure.api_port': 'Network port number for the system to communicate on. Only change this if you know what you\'re doing or have network conflicts. 8000 is the standard.'
};

// Helper function to get tooltip for a setting
export function getSettingTooltip(section, fieldName) {
  const key = `${section}.${fieldName}`;
  return SETTING_TOOLTIPS[key] || 'No additional information available for this setting.';
}

// Function to check if a setting has a tooltip
export function hasTooltip(section, fieldName) {
  const key = `${section}.${fieldName}`;
  return key in SETTING_TOOLTIPS;
}